/**
 * Export Source Loader
 *
 * Load helpers behind deploys and previews (referenced-lesson-packages):
 * - `loadCoursePointers` — expands one normalized bundle into per-locale
 *   `CoursePointer` payloads for the deploy path (no lesson content fetched).
 * - `fetchDraftCoursePreview` — assembles the draft ExportedCourse from live
 *   rows for Course Preview.
 * - `fetchLessonState` — a lesson's resolved learner-shape state for preview.
 * - `fetchLessonExportBundle` — all localized runtimes for one package build.
 */

import type {
  Course,
  CoursePointer,
  CoursePointerBundle,
  ExportedCourse,
  NarrationManifest,
} from '@course-authoring/shared-types';
import { isLessonExportBundle, isCoursePointerBundle, isValidNarrationManifest } from '@course-authoring/shared-types';
import { deserializeLessonFromObjectLenient } from '../store/serialization';
import type { ContentState } from '../store/editorStore';
import { buildDraftCoursePreview } from '../utils/coursePreview';
import { toLearnerContentState } from '../utils/learnerContentState';
import { fetchWithAuthRetry } from './apiUtils';
import { PublishApiError } from './publishApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

async function fetchCourseRecord(courseId: string, locale?: string, accountId: number | null = null): Promise<Course> {
  const params = new URLSearchParams();
  if (locale) params.set('locale', locale);
  if (accountId !== null) params.set('accountId', String(accountId));
  const qs = params.toString();
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/courses/${courseId}${qs ? `?${qs}` : ''}`);
  if (!response.ok) {
    throw new Error(`Failed to load course record (${response.status})`);
  }
  return response.json();
}

/**
 * Fetches a lesson's resolved state in the LEARNER shape: account reads return
 * the editor shape (client-suppressed blocks included, flagged), so the result
 * is passed through `toLearnerContentState` — suppressed blocks omitted and
 * client-context provenance markers stripped. Shared by the draft course
 * loader (preview + export).
 */
export interface LessonRuntimeData {
  contentState: ContentState;
  narration?: NarrationManifest;
}

/**
 * Fetch a learner-shaped lesson while keeping derived runtime data outside the
 * editor ContentState. Callers that only edit/save content should continue to
 * use fetchLessonState.
 */
export async function fetchLessonRuntime(
  lessonId: string,
  locale?: string,
  accountId: number | null = null,
): Promise<LessonRuntimeData> {
  const params = new URLSearchParams();
  if (locale) params.set('locale', locale);
  if (accountId !== null) params.set('accountId', String(accountId));
  const qs = params.toString();
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}${qs ? `?${qs}` : ''}`);
  if (response.status === 403 && accountId !== null) {
    // The account is not assigned to this lesson: preview it as base (the
    // shipped lesson-scope fallback, until NA-195 auto-assigns contained
    // lessons on course assignment).
    return fetchLessonRuntime(lessonId, locale);
  }
  if (!response.ok) {
    throw new Error(`Failed to load lesson ${lessonId} (${response.status})`);
  }
  const data = await response.json();
  const result = deserializeLessonFromObjectLenient(data);
  if (!result.success) {
    throw new Error(
      result.errors.map((error) => error.message).join(', ') || 'Invalid lesson data'
    );
  }
  return {
    contentState: toLearnerContentState(result.data),
    ...(isValidNarrationManifest(data.narration) ? { narration: data.narration } : {}),
  };
}

/** Complete scoped export input. A denied account must never fall back to Standard. */
export async function fetchLessonExportBundle(lessonId: string, accountId: number | null = null) {
  const query = accountId === null ? '' : `?accountId=${accountId}`;
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/lessons/${lessonId}/export-bundle${query}`);
  if (!response.ok) throw new PublishApiError(`Failed to load lesson export bundle (${response.status})`, response.status, 'UNKNOWN');
  const data: unknown = await response.json();
  if (!isLessonExportBundle(data) || data.lessonId !== lessonId || data.accountId !== accountId) {
    throw new Error('Invalid lesson export bundle');
  }
  const variants = data.variants.map((variant) => {
    const result = deserializeLessonFromObjectLenient(variant.lesson);
    if (!result.success) throw new Error('Invalid lesson export bundle content');
    return {
      locale: variant.locale,
      contentState: toLearnerContentState(result.data),
      ...(variant.narration ? { narration: variant.narration } : {}),
    };
  });
  return { defaultLocale: data.defaultLocale, availableLocales: data.availableLocales, variants };
}

export async function fetchLessonState(
  lessonId: string,
  locale?: string,
  accountId: number | null = null,
): Promise<ContentState> {
  return (await fetchLessonRuntime(lessonId, locale, accountId)).contentState;
}

/**
 * Loads one normalized, account-scoped pointer bundle and expands its ordered
 * localized metadata into `CoursePointer` payloads. Invariant settings and
 * root-relative lesson refs are fetched once; no lesson content is fetched.
 */
export async function loadCoursePointers(
  courseId: string,
  accountId: number | null = null,
): Promise<CoursePointer[]> {
  const params = new URLSearchParams();
  if (accountId !== null) params.set('accountId', String(accountId));
  const qs = params.toString();
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/courses/${courseId}/pointer-bundle${qs ? `?${qs}` : ''}`
  );
  if (!response.ok) {
    throw new PublishApiError(`Failed to load course pointer bundle (${response.status})`, response.status, 'UNKNOWN');
  }
  const data: unknown = await response.json();
  if (!isCoursePointerBundle(data) || data.id !== courseId) {
    throw new Error('Invalid course pointer bundle');
  }

  const bundle: CoursePointerBundle = data;
  return bundle.localizedMetadata.map((metadata): CoursePointer => {
    return {
      kind: 'course-pointer',
      id: bundle.id,
      numericId: bundle.numericId,
      title: metadata.title,
      description: metadata.description,
      locale: metadata.locale,
      defaultLocale: bundle.defaultLocale,
      availableLocales: bundle.availableLocales.map((option) => ({ ...option })),
      lessonRefs: bundle.lessonRefs.map((reference) => ({ ...reference })),
      completionCertificateEnabled: bundle.settings.completionCertificateEnabled,
      certificateMessage: metadata.certificateMessage,
      completedMessage: metadata.completedMessage,
      timedCourse: bundle.settings.timedCourse,
      durationMinutes: bundle.settings.durationMinutes,
      minimumRequiredMinutes: bundle.settings.minimumRequiredMinutes,
    };
  });
}

export interface DraftCoursePreviewData {
  course: Course;
  exportedCourse: ExportedCourse;
}

/**
 * Fetches the course record + all lesson drafts and assembles the draft
 * preview. Shared by the draft export source and CourseLearnerPreviewPage.
 */
export async function fetchDraftCoursePreview(
  courseId: string,
  locale?: string,
  accountId: number | null = null,
): Promise<DraftCoursePreviewData> {
  const course = await fetchCourseRecord(courseId, locale, accountId);
  // Client-context reads include suppressed lessons (flagged for the editor's
  // greyed rows); the learner preview and the account-resolved export/deploy
  // builds both omit them.
  const previewCourse = accountId !== null && course.lessons
    ? {
        ...course,
        lessonIds: course.lessons
          .filter((lesson) => lesson.customizationState !== 'suppressed')
          .map((lesson) => lesson.id),
        lessons: course.lessons.filter((lesson) => lesson.customizationState !== 'suppressed'),
      }
    : course;
  const lessonRuntimeData = await Promise.all(
    previewCourse.lessonIds.map((id) => fetchLessonRuntime(id, locale, accountId))
  );

  return {
    course: previewCourse,
    exportedCourse: buildDraftCoursePreview(previewCourse, lessonRuntimeData),
  };
}
